// EAA-1667: The one wrapper that mounts the shared core (theme + permissions +
// auth capability) for web-content. New apps mount their own equivalent — build a
// PermissionState from their store/SSO source, pass their theme layers/config — so
// "every app is wrapped by core" without duplicating the theming/permission engine.
// Must render inside the Redux <Provider>, since it reads app state.
import React, { useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { CoreProvider, useColors, useAntdTheme } from '@ai-content-hub/hooks';
import { UIColorsProvider } from '@ai-content-hub/ui/theme';
import { UploadLimitsProvider } from '@ai-content-hub/shared-config';

import { useSelectedTenantStore } from '@/store/selectedTenantStore';
import { useAuthStore } from '@/store/authStore';
import { useUploadLimitsQuery } from '@/data/systemSettings';
import { useWebContentPermissions } from '@/hooks/useWebContentPermissions';
import { useEmbedThemeModeSync } from '@/hooks/useEmbedThemeModeSync';
import { getThemeConfig, applyOverridesToColors } from '@/styles/theme';
import { instanceTheme } from '@/config/themeInstance';
import { webContentThemePreset } from '../../theme.config';

// EAA-1667: bridges the core theme's resolved colors into packages/ui's
// UIColorsContext seam. Kept app-side (not inside CoreProvider) so packages/hooks
// never imports packages/ui — that would make the two packages circular.
const UIColorsBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const colors = useColors();
    return <UIColorsProvider colors={colors}>{children}</UIColorsProvider>;
};

/**
 * EAA-1212: publishes the server's configured upload limits to every shared upload
 * site (packages may not call the API themselves). Until they arrive — or if the read
 * fails — the seam reports "unknown" and each site keeps its own constant.
 *
 * Not asked in an embed session: an embed token that the endpoint answers with 401
 * would send the client down its refresh-then-logout path, and an editor iframe
 * losing its session over an optional read is far worse than using the constants.
 */
const UploadLimitsBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const authMode = useAuthStore((s) => s.authMode);
    const limits = useUploadLimitsQuery(isAuthenticated && authMode !== 'embed_api');
    return <UploadLimitsProvider limits={limits}>{children}</UploadLimitsProvider>;
};

/**
 * EAA-755: relays light/dark toggles into the ACH embed iframes. Renders
 * nothing — it has to sit inside CoreProvider to read the theme mode, and the
 * embeds it talks to are spread across unrelated subtrees.
 */
const EmbedThemeModeBridge: React.FC = () => {
    useEmbedThemeModeSync();
    return null;
};

// EAA-1804: mounts antd's themed ConfigProvider for the WHOLE route tree.
// It used to live inside MainLayout and IframeLayout, which left every route
// rendered outside a layout — `/login` and `/callback` — on antd's default
// light algorithm. Those pages still got dark CSS variables from
// `<html data-theme="dark">`, so in dark mode the card painted dark navy while
// antd drew the heading at rgba(0,0,0,.88) on top of it: dark text on a dark
// card. Mounting it here means a route cannot opt out of the theme by not
// having a layout.
const AntdThemeBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const themeConfig = useAntdTheme();

    // EAA-1988: antd's STATIC methods — `Modal.confirm`, `message`, `notification` —
    // render outside the React tree, so they never see the ConfigProvider above and
    // fall back to antd's stock blue. That is why every confirm dialog in the app
    // drew a blue "Xác nhận" next to our own purple primary buttons. `holderRender`
    // wraps the static render root in the same theme, which fixes all of them at
    // once rather than each call site passing `okButtonProps` and the next new one
    // forgetting.
    React.useEffect(() => {
        ConfigProvider.config({
            holderRender: (children) => (
                <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
            ),
        });
    }, [themeConfig]);

    return <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>;
};

export const WebContentCoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const permissions = useWebContentPermissions();
    // EAA-1664: tenant id scopes the tenant-theme cache slot; passed into the core
    // theme engine instead of read from Redux inside it.
    const tenantId = useSelectedTenantStore((s) => s.selectedTenantId);

    const theme = useMemo(
        () => ({
            appPreset: webContentThemePreset,
            instance: instanceTheme,
            tenantId,
            getThemeConfig,
            applyOverridesToColors,
        }),
        [tenantId]
    );

    return (
        <CoreProvider permissions={permissions} theme={theme}>
            <AntdThemeBridge>
                <EmbedThemeModeBridge />
                <UIColorsBridge>
                    <UploadLimitsBridge>{children}</UploadLimitsBridge>
                </UIColorsBridge>
            </AntdThemeBridge>
        </CoreProvider>
    );
};
